import React from 'react';
import { LinearProgress, Text, Box } from '@/theme/index';
import { useCurrentUserApiAccess } from '../hooks/useCurrentUserApiAccess/useCurrentUserApiAccess';

export function ApiStatus() {
  const {
    currentApiPlanMonthlyUsageDescription,
    currentUserMonthlyReportLimits,
    isLoading,
  } = useCurrentUserApiAccess();
  const {
    isUnlimited,
    monthlyReportsLimit,
    monthlyReportsRemaining,
    monthlyCreditsLimit,
    monthlyCreditsRemaining,
  } = currentUserMonthlyReportLimits;

  const reportsUsed = () => {
    if (isUnlimited) return 'Unlimited';

    return (
      (monthlyReportsLimit as number) - (monthlyReportsRemaining as number)
    );
  };

  const creditsUsed = () => {
    if (isUnlimited) return 'Unlimited';

    if (!monthlyCreditsLimit || !monthlyCreditsRemaining) {
      return 0;
    }
    return (monthlyCreditsLimit as number) - monthlyCreditsRemaining;
  };

  const reportsUsedPercentage = () => {
    if (isUnlimited || !monthlyReportsLimit || !monthlyReportsRemaining) {
      return 0;
    }

    const percentage =
      ((reportsUsed() as number) / (monthlyReportsLimit as number)) * 100;

    return percentage;
  };

  const creditsUsedPercentage = () => {
    if (isUnlimited || !monthlyCreditsLimit || !monthlyCreditsRemaining) {
      return 0;
    }

    return ((creditsUsed() as number) / (monthlyCreditsLimit as number)) * 100;
  };

  if (isLoading) return <Text>Loading...</Text>;

  return (
    <Box>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(0.5),
        })}
      >
        <Text variant="h3" fontWeight="500">
          Current plan
        </Text>
        <Text variant="h3" fontWeight="bold">
          {currentApiPlanMonthlyUsageDescription()}
        </Text>
      </Box>

      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(3),
        })}
      >
        {/* API Credits */}
        <Box>
          <LinearProgress
            variant="determinate"
            value={creditsUsedPercentage()}
          />
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
            })}
            data-cy="api-status-credits-used"
          >
            <Text>{creditsUsed()}</Text>
            <Text>out of</Text>
            <Text>{monthlyCreditsLimit}</Text>
            <Text>credits used</Text>
          </Box>
        </Box>

        {/* PDF Credits */}
        <Box>
          <LinearProgress
            variant="determinate"
            value={reportsUsedPercentage()}
          />
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
            })}
            data-cy="api-status-reports-used"
          >
            <Text>{reportsUsed()}</Text>
            <Text>out of</Text>
            <Text>{monthlyReportsLimit}</Text>
            <Text>reports accessed</Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
